import { types, Instance, flow, applySnapshot } from 'mobx-state-tree';
import { isBetweenNowAnd24HoursFromNow } from '../utils/utilsFunctions';

import {
  getGroupsByNext,
  getUserGroupsByDiscovery,
  getUserCurrentGroups,
  findGroupsOptions,
  getOpenGroupsBySegment,
} from './apiWrappers/wrappers/useGroup';
import { UserOldGroupsResponse } from './types/useGroup.types';

const MAX_ROOMS_TO_DISPLAY = 5;

const OldGroupModel = types.model('OldGroupModel', {
  groupInstanceId: types.identifier,
  leaderName: types.string,
});

const GroupModel = types.model('GroupModel', {
  id: types.identifier,
  schedule: types.string,
  startDate: types.string,
  leaderImg: types.string,
  leaderName: types.string,
  leaderId: types.string,
  leaderAgenda: types.maybeNull(types.string),
  intro: types.string,
  shortIntro: types.string,
  title: types.string,
  description: types.maybeNull(types.string),
  activeMembers: types.number,
  payingMembersCount: types.number,
});

interface Group extends Instance<typeof GroupModel> {}

const ScheduleModel = types.model('ScheduleModel', {
  hour: types.number,
  local: types.maybeNull(
    types.model({
      tz: types.string,
      hour: types.number,
      minute: types.number,
      weekday: types.number,
    })
  ),
  minute: types.number,
  weekday: types.number,
  duration: types.number,
});

const OpenGroupModel = types.model('OpenGroupModel', {
  groupId: types.identifier,
  schedule: types.maybeNull(types.array(ScheduleModel)),
  startDate: types.optional(types.string, ''),
  leaderImg: types.optional(types.string, ''),
  leaderName: types.optional(types.string, ''),
  segments: types.optional(types.array(types.string), []),
  title: types.optional(types.string, ''),
  nextSession: types.maybeNull(types.model({ isoString: types.string, ts: types.number })),
  isGroupFacilitator: types.optional(types.boolean, false),
  tagline: types.maybeNull(types.string),
});

export interface OpenGroup extends Instance<typeof OpenGroupModel> {}

const GroupStoreModel = types
  .model('GroupsStoreModel', {
    groups: types.array(GroupModel),
    oldGroups: types.maybeNull(types.array(OldGroupModel)),
    openGroups: types.optional(types.array(OpenGroupModel), []),
  })
  .views((self) => ({
    getGroup(groupId: string) {
      return self.groups.find((group) => group.id === groupId);
    },
  }))
  .actions((self) => ({
    getGroupsByNext: flow(function* fetchData(url: string) {
      const { kind, data } = yield getGroupsByNext(url);
      if (kind !== 'ok') {
        return;
      }

      applySnapshot(self, { ...self, groups: data.groups });
    }),
    getUserGroupsByDiscovery: flow(function* fetchData() {
      const { kind, data } = yield getUserGroupsByDiscovery();
      if (kind !== 'ok') {
        return;
      }

      applySnapshot(self, { ...self, groups: data });
    }),
    getUserCurrentGroups: flow(function* fetchData() {
      const { kind, data } = yield getUserCurrentGroups();
      if (kind !== 'ok') {
        return;
      }

      const oldGroups: UserOldGroupsResponse[] = data;
      const groupIdArray = oldGroups.map((group) => {
        return { groupInstanceId: group.groupInstanceId, leaderName: group.leaderName };
      });
      applySnapshot(self, { ...self, oldGroups: groupIdArray });
    }),
    findGroupsOptions: flow(function* fetchData() {
      const { kind, data } = yield findGroupsOptions();
      if (kind !== 'ok') {
        return;
      }

      applySnapshot(self, { ...self, groups: data.groups });
    }),
    getOpenGroupsBySegment: flow(function* fetchData(segment: string) {
      const { kind, data: groups } = yield getOpenGroupsBySegment(segment);
      if (kind !== 'ok') {
        return;
      }
      const suggestedGroups = groups
        .filter((group: OpenGroup) => isBetweenNowAnd24HoursFromNow(group.nextSession?.isoString as string))
        .slice(0, MAX_ROOMS_TO_DISPLAY);

      applySnapshot(self, { ...self, openGroups: suggestedGroups });
    }),
  }));

export { GroupModel, GroupStoreModel, Group };
